import { useCallback, useMemo, useRef, useState } from 'react'
import { ApolloQueryResult, QueryResult } from '@apollo/client'
import GlobalCaseSearchOption from '@broker-crm-common/GlobalSearch/GlobalCaseSearchOption/GlobalCaseSearchOption'
import { getMultipleClientNameTag } from '@broker-crm-common/GlobalSearch/GlobalCaseSearchOption/GlobalCaseSearchOption.helpers'
import GlobalSearchModal from '@broker-crm-common/GlobalSearch/GlobalSearchModal/GlobalSearchModal'
import GlobalSearchOption from '@broker-crm-common/GlobalSearch/GlobalSearchOption/GlobalSearchOption'
import { getUrlForOption } from '@broker-crm-common/GlobalSearch/GlobalSearchOption/GlobalSearchOption.helpers'
import Header from '@broker-crm-common/GlobalSearch/GlobalSearchRecentCases/Header'
import { useGetFormattedRecentCasesOptions } from '@broker-crm-common/GlobalSearch/GlobalSearchRecentCases/useGetFormattedRecentCasesOptions'
import GlobalSearchToggleButtonGroup, {
  GlobalSearchType,
} from '@broker-crm-common/GlobalSearch/GlobalSearchToggleButtonGroup/GlobalSearchToggleButtonGroup'
import useGlobalCaseSearch from '@broker-crm-hooks/useGlobalCaseSearch'
import useGlobalSearch from '@broker-crm-hooks/useGlobalSearch'
import { useMatomo } from '@jonkoops/matomo-tracker-react'
import isEmpty from 'lodash/isEmpty'
import { useLocation, useNavigate } from 'react-router-dom'

import { useFormatMessage } from '@acre/utils'
import {
  Exact,
  GetCaseSearchResultsQuery,
  GetCaseSearchResultsQueryResult,
  GetSearchResultsQueryResult,
  InputMaybe,
  Maybe,
  useGetCaseSearchResultsLazyQuery,
  useGetSearchResultsLazyQuery,
} from '@acre/graphql'
import { AutocompleteOption, GlobalSearchField, GlobalSearchFieldProps, trackEventFiltered } from '@acre/design-system'
import { CaseSearchResultItem, SearchResultItem } from '@acre/graphql/types'

interface SearchOptionType extends AutocompleteOption, SearchResultItem {
  url?: string
}

interface CaseSearchOptionType extends AutocompleteOption, CaseSearchResultItem {
  url?: string
}

type OptionType = SearchOptionType | CaseSearchOptionType

interface NavGlobalSearchModalProps {
  isModalSearchVisible: boolean
  closeModalSearch: () => void
}

const NavGlobalSearchModal = ({ isModalSearchVisible, closeModalSearch }: NavGlobalSearchModalProps) => {
  const formatMessage = useFormatMessage()
  const navigate = useNavigate()
  const recentCasesRef = useRef<HTMLDivElement>(null)
  const [searchInput, setSearchInput] = useState<Maybe<string>>(null)

  const location = useLocation()

  const globalSearchParams = useGlobalSearch({
    query: useGetSearchResultsLazyQuery,
  })

  const globalCaseSearchParams = useGlobalCaseSearch({
    query: useGetCaseSearchResultsLazyQuery,
  })

  const { trackEvent } = useMatomo()

  const [selectedSearchType, setSelectedSearchType] = useState<GlobalSearchType>('recents')

  const isCaseSearch = selectedSearchType === 'cases'
  const isRecentView = selectedSearchType === 'recents'

  const handleSelectionChange = useCallback(
    (selected: GlobalSearchType) => {
      setSelectedSearchType(selected)
      trackEventFiltered(trackEvent, location, 'searchAction', 'searchButtonClick', `selectedSearchType: ${selected}`)
    },
    [location, trackEvent],
  )

  const handleMatomoTrackEvent = useCallback(
    (actionName: string, url: string) => {
      trackEventFiltered(trackEvent, location, 'searchAction', actionName, `navigate-${url}`)
    },
    [location, trackEvent],
  )

  const getOptionPlaceholderLabel = useCallback((option: any) => {
    // Show client names for recent viewed cases options
    if (option.doctype === 'client_case' && option.overrideName) {
      return option.overrideName
    }

    // For case search, show the clients name as placeholder in the search input
    if (option.doctype === 'case' && option?.Clients) return getMultipleClientNameTag(option.Clients)

    // For client search, show the client name as placeholder for client options
    if (option.doctype === 'client' || (option.doctype === 'client_case' && option.clientNameTag)) {
      return option.clientNameTag
    }

    // No placeholder for case options
    return ''
  }, [])

  const formattedRecentCasesOptions = useGetFormattedRecentCasesOptions()

  const getSearchOptions = useCallback(
    (
      options:
        | QueryResult<GetCaseSearchResultsQuery, Exact<{ searchFragment: string; bookmark?: InputMaybe<number> }>>
        | ApolloQueryResult<GetCaseSearchResultsQuery>,
    ) => {
      const redirectLink = isCaseSearch
        ? (options as GetCaseSearchResultsQueryResult).data?.caseSearch?.link
        : (options as GetSearchResultsQueryResult).data?.search?.link

      // When searching by a case id or sale id, redirect to the case overview page
      if (redirectLink) {
        navigate(redirectLink)
        closeModalSearch()
        setSearchInput(null)
        return [] as OptionType[]
      }

      // Return case search results when searching cases
      if (isCaseSearch) {
        return globalCaseSearchParams.getOptions(options as GetCaseSearchResultsQueryResult).map((option) => ({
          ...option,
          url: `/cases/${option.id}/overview`,
        })) as OptionType[]
      }

      // Return regular search results for client search
      return globalSearchParams.getOptions(options as GetSearchResultsQueryResult).map((option) => ({
        ...option,
        url: getUrlForOption(option) ?? '',
      })) as unknown as OptionType[]
    },
    [closeModalSearch, globalCaseSearchParams, globalSearchParams, isCaseSearch, navigate],
  )

  // For recents tab without search input, we need to provide the options directly
  const searchFieldProps = useMemo(
    () => ({
      ...(isRecentView
        ? {
            options: formattedRecentCasesOptions.map((o) => ({ ...o, url: `/cases/${o.id}/overview` })) as OptionType[],
            loading: false,
            open: true,
          }
        : {
            ...(isCaseSearch ? globalCaseSearchParams : globalSearchParams),
            getOptions: (options: any) => getSearchOptions(options),
          }),
    }),
    [
      formattedRecentCasesOptions,
      getSearchOptions,
      globalCaseSearchParams,
      globalSearchParams,
      isCaseSearch,
      isRecentView,
    ],
  )

  const handleInputChange = useCallback<
    Required<GlobalSearchFieldProps<OptionType, false, false, true>>['onInputChange']
  >((event, newInputValue) => {
    setSearchInput(newInputValue)
  }, [])

  const handleChange = useCallback<Required<GlobalSearchFieldProps<OptionType, false, false, true>>['onChange']>(
    (event, value, reason) => {
      // If the user clears the search input when selecting CLIENTS, reset the search type to 'recents'
      if (reason === 'clear' && selectedSearchType === 'clients') {
        handleSelectionChange('recents')
      }

      if (value && typeof value === 'object' && 'url' in value && value?.url && value?.url !== '') {
        // Track result from case search
        value.doctype === 'case' && handleMatomoTrackEvent('viewCaseSearchResult', value.url)

        // Track result from client search
        value.doctype === 'client' && handleMatomoTrackEvent('viewClientClientSearchResult', value.url)
        value.doctype === 'client_case' && handleMatomoTrackEvent('viewCaseClientSearchResult', value.url)

        // Track result from recently viewed cases
        isRecentView && handleMatomoTrackEvent('viewRecentlyViewedCase', value.url)

        closeModalSearch()
        navigate(value.url)
        setSearchInput(null)
      }
    },
    [closeModalSearch, handleMatomoTrackEvent, handleSelectionChange, isRecentView, navigate, selectedSearchType],
  )

  return (
    <GlobalSearchModal open={isModalSearchVisible} onClose={closeModalSearch}>
      <GlobalSearchField
        key="NavGlobalSearchField"
        id="NavGlobalSearchField"
        name="NavGlobalSearchField"
        placeholder={isCaseSearch ? formatMessage('nav.search.caseSearch') : formatMessage('nav.search.search')}
        noOptionsText={
          isRecentView ? formatMessage('nav.search.noRecentlyViewedCases') : formatMessage('nav.search.noResults')
        }
        getOptionLabel={getOptionPlaceholderLabel}
        onInputChange={handleInputChange}
        onChange={handleChange}
        {...searchFieldProps}
        // Recently viewed items all share the same doctype
        // Group them together to display under a single "Recently Viewed Cases" header
        groupBy={
          isRecentView
            ? (option) => {
                return option.doctype
              }
            : undefined
        }
        renderGroup={(params) => (
          <>
            <Header />
            {params.children}
          </>
        )}
        renderOption={isCaseSearch || isRecentView ? GlobalCaseSearchOption : GlobalSearchOption}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        TextFieldProps={{
          inputProps: {
            autoFocus: true,
            tabIndex: 0,
            onPaste: () => {
              // Handle paste event - this fires before the input value is updated
              if (isRecentView) {
                handleSelectionChange(isCaseSearch ? 'cases' : 'clients')
              }
            },
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Tab' && !isEmpty(searchInput)) {
                e.preventDefault()
              }
              if ((e.key === 'ArrowDown' || e.key === 'Tab') && !e.shiftKey && recentCasesRef.current) {
                e.preventDefault()
                recentCasesRef.current.focus()
              }

              // Switch type after keydown and before search input value is updated
              // Triggers on any printable character (letters, numbers, special chars, space)
              if (isRecentView && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                handleSelectionChange(isCaseSearch ? 'cases' : 'clients')
              }
            },
          },
        }}
        rightActions={
          <GlobalSearchToggleButtonGroup selectedType={selectedSearchType} onSelectionChange={handleSelectionChange} />
        }
      />
    </GlobalSearchModal>
  )
}

export default NavGlobalSearchModal
